/**
 * Target Selector Component
 * Select notification target type and users
 */

import { useCallback } from 'react';
import { Select } from '@/components/ui/Select';
import { useI18n } from '@/i18n';
import { UserSelector } from './UserSelector';

export interface TargetSelection {
  type: 'all' | 'user' | 'test';
  user_ids?: string[];
}

export interface TargetSelectorProps {
  value: TargetSelection;
  onChange: (value: TargetSelection) => void;
  error?: string;
}

export function TargetSelector({ value, onChange, error }: TargetSelectorProps) {
  const { messages } = useI18n();
  const t = messages.pushNotifications;
  const tc = messages.common;

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as TargetSelection['type'];
    onChange({
      type: newType,
      user_ids: newType === 'user' ? [] : undefined,
    });
  };

  const handleUserIdsChange = useCallback(
    (userIds: string[]) => {
      onChange({
        type: 'user',
        user_ids: userIds.length > 0 ? userIds : undefined,
      });
    },
    [onChange]
  );

  return (
    <div className="space-y-4">
      <Select
        label={t.sendForm.target}
        value={value.type}
        onChange={handleTypeChange}
        options={[
          { value: 'all', label: t.sendForm.targetAll },
          { value: 'user', label: t.sendForm.targetSpecificUsers },
          { value: 'test', label: t.sendForm.targetTest },
        ]}
        required
        error={error}
      />

      {value.type === 'user' && (
        <UserSelector
          selectedUserIds={value.user_ids ?? []}
          onChange={handleUserIdsChange}
        />
      )}

      {value.type === 'all' && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            <strong>{tc.warning}:</strong> {t.sendForm.targetAllWarning}
          </p>
        </div>
      )}

      {value.type === 'test' && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>{t.sendForm.targetTest}:</strong> {t.sendForm.targetTestDesc}
          </p>
        </div>
      )}
    </div>
  );
}
